import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray, transformerObject, } from '../transformer';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';

type ProductSliceState = {
  loading: boolean;
  listDishAge: DishAge[];
  listDishCookingMethod: DishCookingMethod[];
  listSourceDish: ListSourceDish[];
  listSizeDish: SizeDish[];

  listProduct: ProductList[];
  listGroupFood: GroupListFood[];
  listProductById?: ProductList;
  listHistoryProduct?: HistoryProduct[];
  paginationHistory: Pagination;

  listStructureMealDish: StructureMealDish[];
  message?: MessageType;
  pagination: Pagination;
  fileUrl: string;
  downloadTemplateProduct?: TemplateFood;
};

const initialState: ProductSliceState = {
  loading: false,
  listDishAge: [],
  listDishCookingMethod: [],
  listSourceDish: [],
  listSizeDish: [],

  listProduct: [],
  listGroupFood: [],

  listStructureMealDish: [],
  pagination: PAGINATION,
  paginationHistory: PAGINATION,
  fileUrl: '',
  downloadTemplateProduct: undefined
};

export const productSlice = createAppSlice({
  name: 'product',
  initialState,
  reducers: (create) => ({
    getListProduct: create.asyncThunk(
      async ({
        query,
        offset,
        limit
      }: { query?: FilterProduct; offset?: number; limit?: number }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('page', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `product-management/list${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await apiFetcher.post(url, query || {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listProduct = transformerArray(action.payload.listData);
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listProduct = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    deleteProduct: create.asyncThunk(
      async (id: number) => {
        await apiFetcher.delete(`product-management/${id}`);
        return id
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listProduct = state.listProduct.filter(
            (product) => product.id !== String(action.payload)
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_PRODUCT_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_PRODUCT_FAIL
          };
        }
      }
    ),
    getListFood: create.asyncThunk(
      async (payload?: { offset?: number; limit?: number }) => {
        const { offset = 1, limit = 10 } = payload || {};
        const params = new URLSearchParams();
        params.append('offset', offset.toString());
        params.append('limit', limit.toString());

        const url = `foods/filter?${params.toString()}`;
        const response = await apiFetcher.post(url, {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listGroupFood = transformerArray(action.payload.listData);
        },
        rejected: (state) => {
          state.loading = false;
          state.listGroupFood = [];
        }
      }
    ),
    createProduct: create.asyncThunk(
      async (query: Product, { rejectWithValue }) => {
        try {
          const response = await apiFetcher.post(
            'product-management', 
            query,
            {
              headers: {
                'Content-Type': 'application/json'
              }
            }
          );
          return response.data;
        } catch (err: any) {
          const message = err?.response?.data?.message;
          return rejectWithValue(message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_PRODUCT_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          let errorMsg = MESSAGE.CREATE_PRODUCT_FAIL;
          if (typeof action.payload === 'string') {
            errorMsg = action.payload;
          }
          state.message = {
            type: Message.ERROR,
            message: errorMsg
          };
        }
      }
    ),
    getDetailProduct: create.asyncThunk(
      async (id: string) => {
        const response = await apiFetcher.get(`product-management/${id}`);
        return response.data[0];
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listProductById = transformerObject(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.listProductById = undefined;
        }
      }
    ),
    editProduct: create.asyncThunk(
      async (
        { id, payload }: { id: string; payload: Record<string, any> },
        { rejectWithValue }
      ) => {
        try {
          const response = await apiFetcher.put(`product-management?id=${id}`, payload);
          return response.data;
        } catch (error: any) {
          const message = error?.response?.data?.message;
          return rejectWithValue(message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listProduct = state.listProduct.filter(
            (product) => product.id !== String(action.payload)
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_PRODUCT_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          let errorMsg = MESSAGE.UPDATE_PRODUCT_FAIL;
          if (typeof action.payload === 'string') {
            errorMsg = action.payload;
          }
          state.message = {
            type: Message.ERROR,
            message: errorMsg
          };
        }
      }
    ),
    getHistoryProduct: create.asyncThunk(
      async ({
        offset,
        limit
      }: { offset?: number; limit?: number }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('offset', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `history-import/filter${params.toString() ? `?${params.toString()}` : ''}`;
        const query = {  
          "type": "PRODUCT"
        };
        const response = await apiFetcher.post(url, query || {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHistoryProduct = transformerArray(action.payload.listData);
          state.paginationHistory = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHistoryProduct = [];
          state.paginationHistory = PAGINATION;
        }
      }
    ),
    importFile: create.asyncThunk(
      async (formData: FormData, { rejectWithValue }) => {
        try {
          const response = await apiFetcher.post(
            'product-management/import',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          const result = response.data;
          const firstError = Array.isArray(result?.errors)
            ? result.errors[0]
            : undefined;

          if (firstError) {
            return rejectWithValue(
              `Dòng ${firstError.row}: ${firstError.message}`
            );
          }

          return result;
        } catch (err: any) {
          const message = err?.response?.data?.message;
          return rejectWithValue(message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.IMPORT_PRODUCT_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          let errorMsg = MESSAGE.IMPORT_PRODUCT_FAIL;
          if (typeof action.payload === 'string') {
            errorMsg = action.payload;
          }
          state.message = {
            type: Message.ERROR,
            message: errorMsg
          };
        }
      }
    ),
  
    getProductByFoodId: create.asyncThunk(
      async (foodId: string) => {
        const response = await apiFetcher.get(`product-management/food/${foodId}`);
        return transformerArray(response.data);
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, _action) => {
          state.loading = false;
        },
        rejected: (state) => {
          state.loading = false;
        }
      }
    ),

    downloadTemplateProduct: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('template/info?type=PRODUCT');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.downloadTemplateProduct = action.payload
        },
        rejected: (state) => {
          state.loading = false;
          state.downloadTemplateProduct = undefined
        }
      }
    ),

    resetMessages: create.reducer((state) => {
      state.message = undefined;
    }),
  }),
  selectors: {
    watchListProduct: (product) => product.listProduct,
    watchListHistory: (product) => product.listHistoryProduct,
    watchGroupListFood: (product) => product.listGroupFood,
    watchDetailProductById: (product) => product.listProductById,
    watchProductMessage: (product) => product.message,
    watchProductPagination: (product) => product.pagination,
    watchProductPaginationHistory: (product) => product.paginationHistory,
    watchProductLoading: (product) => product.loading,
    watchDownloadTemplateProduct: (product) => product.downloadTemplateProduct
  }
});

export const {
  watchListProduct,
  watchListHistory,
  watchGroupListFood,
  watchDetailProductById,
  watchProductMessage,
  watchProductPagination,
  watchProductPaginationHistory,
  watchProductLoading,
  watchDownloadTemplateProduct
} = productSlice.selectors;

export const {
  getListProduct,
  deleteProduct,
  getListFood,
  createProduct,
  getDetailProduct,
  editProduct,
  getHistoryProduct,
  importFile,
  resetMessages,
  getProductByFoodId,
  downloadTemplateProduct
} = productSlice.actions;
